import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Alert, Divider, Paper,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import API from '../../services/api';
import MobileDialog from './MobileDialog';

/**
 * Inventory Management Dialog with Take/Add semantics.
 *
 * Originally defined inline in ProductsPage.js. Extracted so the new
 * single-page order flow can reuse the exact same component when the user
 * needs to bump (or take) inventory inline.
 *
 * The user picks "take" or "add", types HOW MUCH to take/add (not the new
 * absolute total), sees a preview of the resulting quantity, then confirms.
 *
 * Props:
 *  - open
 *  - onClose
 *  - product           : { ProductID, ProductName, inventory: [{StorageID, available_quantity, ...}] }
 *  - storages          : full list of storages
 *  - onSuccess         : called after a successful update
 *  - canTakeInventory  : show/hide the "take" button (default true)
 *  - canAddInventory   : show/hide the "add" button (default true)
 *  - initialStorageId  : optional preselected storage id
 */
const InventoryManagementDialog = React.memo(({
  open,
  onClose,
  product,
  storages,
  onSuccess,
  canTakeInventory = true,
  canAddInventory = true,
  initialStorageId = null,
}) => {
  const [selectedStorageId, setSelectedStorageId] = useState(null);
  const [actionType, setActionType] = useState(null); // 'take' or 'add'
  const [quantity, setQuantity] = useState('');
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    if (formError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [formError]);

  // Pick the initial storage when the dialog opens
  useEffect(() => {
    if (!open) return;
    if (initialStorageId) {
      setSelectedStorageId(initialStorageId);
    } else if (storages && storages.length === 1) {
      setSelectedStorageId(storages[0].StorageID);
    } else if (storages && storages.length > 1) {
      const def = storages.find((s) => s.is_default);
      setSelectedStorageId(def ? def.StorageID : storages[0].StorageID);
    }
  }, [open, initialStorageId, storages]);

  // Auto-select action type if only one permission is available
  useEffect(() => {
    if (open) {
      if (canTakeInventory && !canAddInventory) setActionType('take');
      else if (canAddInventory && !canTakeInventory) setActionType('add');
      else setActionType('add'); // sensible default for the order flow
    }
  }, [open, canTakeInventory, canAddInventory]);

  const getCurrentQuantity = () => {
    if (!product || !selectedStorageId) return 0;
    const inv = product.inventory?.find((i) => i.StorageID === selectedStorageId);
    return inv?.available_quantity || 0;
  };

  const getSelectedStorage = () => storages?.find((s) => s.StorageID === selectedStorageId);

  const getNewQuantity = () => {
    const current = getCurrentQuantity();
    const qty = parseFloat(quantity) || 0;
    if (actionType === 'take') return current - qty;
    if (actionType === 'add') return current + qty;
    return current;
  };

  const handleValidation = () => {
    setFormError('');
    if (!selectedStorageId) {
      setFormError('יש לבחור מחסן');
      return;
    }
    if (!actionType) {
      setFormError('יש לבחור פעולה (לקיחה או הוספה)');
      return;
    }
    const qty = parseFloat(quantity);
    if (!quantity || isNaN(qty) || qty <= 0) {
      setFormError('יש להזין כמות חיובית');
      return;
    }
    const newQty = getNewQuantity();
    if (newQty < 0) {
      setFormError(`לא ניתן לקחת ${qty} פריטים. קיימים רק ${getCurrentQuantity()} במלאי`);
      return;
    }
    setConfirmStep(true);
  };

  const handleSubmit = async () => {
    setFormError('');
    setLoading(true);
    try {
      const newQty = getNewQuantity();
      await API.post('/inventory/update/', {
        ProductID: product.ProductID,
        StorageID: selectedStorageId,
        available_quantity: newQty,
      });
      onSuccess?.();
      handleClose();
    } catch (error) {
      setFormError(error?.response?.data?.error || 'שגיאה בעדכון מלאי');
      setConfirmStep(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStorageId(storages?.length === 1 ? storages[0].StorageID : null);
    setActionType(null);
    setQuantity('');
    setFormError('');
    setConfirmStep(false);
    onClose();
  };

  const handleGoBack = () => {
    setConfirmStep(false);
    setFormError('');
  };

  if (!product) return null;

  const currentQty = getCurrentQuantity();
  const selectedStorage = getSelectedStorage();
  const newQty = getNewQuantity();
  const hasMultipleStorages = storages && storages.length > 1;

  return (
    <MobileDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>עדכון מלאי - {product.ProductName}</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert ref={errorRef} severity="error" sx={{ mt: 2, mb: 2 }}>{formError}</Alert>
        )}

        {!confirmStep ? (
          <Box sx={{ mt: 2 }}>
            {hasMultipleStorages ? (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>בחר מחסן</InputLabel>
                <Select
                  value={selectedStorageId || ''}
                  onChange={(e) => setSelectedStorageId(e.target.value)}
                  label="בחר מחסן"
                >
                  {storages.map((storage) => (
                    <MenuItem key={storage.StorageID} value={storage.StorageID}>
                      {storage.StorageName}
                      {storage.is_default && ' (ברירת מחדל)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">מחסן:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedStorage?.StorageName || 'לא נבחר'}
                </Typography>
              </Box>
            )}

            {selectedStorageId && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">כמות נוכחית במלאי</Typography>
                <Typography variant="h4" fontWeight="bold" color="primary">{currentQty}</Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              {canTakeInventory && (
                <Button
                  variant={actionType === 'take' ? 'contained' : 'outlined'}
                  color="error"
                  size="large"
                  fullWidth
                  onClick={() => setActionType('take')}
                  startIcon={<RemoveIcon />}
                  sx={{ py: 2, fontSize: '1.1rem', direction: 'ltr' }}
                >
                  לקיחה
                </Button>
              )}
              {canAddInventory && (
                <Button
                  variant={actionType === 'add' ? 'contained' : 'outlined'}
                  color="success"
                  size="large"
                  fullWidth
                  onClick={() => setActionType('add')}
                  startIcon={<AddIcon />}
                  sx={{ py: 2, fontSize: '1.1rem', direction: 'ltr' }}
                >
                  הוספה
                </Button>
              )}
            </Box>

            <TextField
              fullWidth
              type="number"
              label="כמות"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onFocus={(e) => e.target.select()}
              onWheel={(e) => e.target.blur()}
              inputProps={{ min: 0, step: 1 }}
              sx={{ mb: 2 }}
              disabled={!actionType}
            />

            {actionType && quantity && parseFloat(quantity) > 0 && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: actionType === 'add' ? '#e8f5e9' : '#fff3e0',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">כמות לאחר העדכון</Typography>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={actionType === 'add' ? 'success.main' : 'warning.main'}
                >
                  {newQty}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                textAlign: 'center',
                p: 3,
                backgroundColor: actionType === 'add' ? '#e8f5e9' : '#fff3e0',
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                {actionType === 'take' ? 'לקיחה' : 'הוספה'}
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 'bold', color: actionType === 'add' ? 'success.main' : 'warning.main' }}
              >
                {quantity}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {actionType === 'take' ? 'פריטים מ' : 'פריטים ל'}
                {selectedStorage?.StorageName}
              </Typography>
            </Box>

            <Paper elevation={1} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography color="text.secondary">מוצר:</Typography>
                <Typography fontWeight="bold">{product.ProductName}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography color="text.secondary">כמות לאחר העדכון:</Typography>
                <Typography fontWeight="bold" color={newQty === 0 ? 'warning.main' : 'text.primary'}>
                  {newQty}
                </Typography>
              </Box>
            </Paper>

            {newQty === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">שים לב: הכמות לאחר העדכון תהיה 0</Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!confirmStep ? (
          <>
            <Button onClick={handleClose}>ביטול</Button>
            <Button
              onClick={handleValidation}
              variant="contained"
              color="primary"
              disabled={!actionType || !quantity || loading}
            >
              המשך לאישור
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleGoBack} disabled={loading}>חזרה</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? 'מעדכן...' : 'אישור'}
            </Button>
          </>
        )}
      </DialogActions>
    </MobileDialog>
  );
});

export default InventoryManagementDialog;
